/** biome-ignore-all lint/nursery/noExcessiveLinesPerFile: TODO: shorten this file on CD */
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { isBoolean, isEmpty } from "lodash";
import { ArrowLeft, ExternalLink, Link as LinkIcon } from "lucide-react";
import { type ReactElement, type ReactNode, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Element } from "react-scroll";

import Preloader from "@krea/customer-features/shared-components/preloader";
import { BankingDataChart } from "@krea/lender-features/components/banking-data-chart";
import { Alert } from "@krea/shared/design-system/alerts/Alert";
import { Button } from "@krea/shared/design-system/Button";
import { Form } from "@krea/shared/design-system/Form";
import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";
import { Table } from "@krea/shared/design-system/table";
import { useAuthStore } from "@krea/shared/features/auth/authStore";
import {
  CountryCode,
  Currency,
  EnvironmentEnum,
} from "@krea/shared/types/common";
import { cn } from "@krea/shared/utils";
import { getAppUrls } from "@krea/shared/utils/get-app-urls";

import {
  BidTypeEnum,
  LenderApplicationTypeEnum,
} from "@/features/common/enums";
import { useGetLenderApplicationDetailQuery } from "@/features/lender-applications/hooks/data/use-get-lender-application-detail.query";
import type { ILenderApplicationExtended } from "@/features/lender-applications/types/lender-application";
import type { IInsightsItem } from "@/features/lender-applications/types/loan-application";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import type {
  FieldRequestObject,
  GroupedFieldRequestSubmittedDataChoice,
  IFieldRequest,
} from "@/features/lender-responses/types/field-request";
import type { ILenderResponse } from "@/features/lender-responses/types/lender-response";
import { useGetMeQuery } from "@/features/users/hooks/use-get-me.query";
import { bidValidationSchema } from "@/validations";

import { useGetAccountingData } from "../../store/accounting-data";
import { formatTime, ScrollToElement, timeTo, ucFirst } from "../../utils";
import { useFormatCurrency } from "../../utils/customHooks/useFormatCurrency";
import { useMobile } from "../../utils/customHooks/useMobile";
import DetailsCard from "../details/card";
import DetailsContainer from "../details/container";
import { DetailsItemGridData } from "../details/item-grid/data";
import { DetailsItemGrid } from "../details/item-grid/grid";
import { DetailsItemList } from "../details/item-list/index";
import { HighlightedCustomerSegmentIndicator } from "../highlighted-customer-segment-indicator";
import { Indicator } from "../requests/indicator/index";
import { StatusLabel } from "../status-label";
import { AccountingDataChart } from "./accounting-data-chart";
import { AttachedFile } from "./attached-file";
import Bids from "./bids";
import { FormSwitcher } from "./bids/FormSwitcher";
import { LenderResponse } from "./bids/LenderResponse";
import { CreditCaseFileBlock } from "./blocks/CreditCaseFileBlock";
import { CompanyStructure } from "./company-structure";
import { LenderNotes } from "./lender-notes";
import { useLenderApplicationDetailsStore } from "./lenderApplicationDetailsStore";
import { StructuredData } from "./structured-data";
import Toolbar from "./toolbar";

const ToolbarTime = ({
  title,
  time,
  timeWarning,
  className,
}: {
  title?: string;
  time?: ReactNode;
  timeWarning?: boolean;
  className?: string;
}) => {
  return (
    <dl className={cn("flex items-center mx-0 my-1", className)}>
      <dt className="m-0 font-normal">{`${title}:`}</dt>
      <dd
        className={cn("m-0 ml-1 font-normal", {
          "text-gray-800": !timeWarning,
          "text-red-500": timeWarning,
        })}
      >
        {time}
      </dd>
    </dl>
  );
};

const AttachedLink = ({ value }: { value: string }) => {
  let href: string;

  try {
    href = new URL(value).href;
  } catch (e: any) {
    if (e.message.includes(" Invalid URL")) href = `//${value}`;
    else href = "";
  }

  const linkContent = (
    <div className="my-1 flex items-center overflow-hidden pr-3">
      <LinkIcon className="size-4" />
      <a
        className="block overflow-hidden overflow-ellipsis whitespace-nowrap"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ maxWidth: "400px" }}
      >
        {value}
      </a>
    </div>
  );

  return (
    <div className="flex flex-wrap items-center">
      <ResponsiveTooltip trigger={linkContent} hidden={value.length <= 46}>
        {value}
      </ResponsiveTooltip>
    </div>
  );
};

const TextItem = ({
  text,
  notExists,
  value,
  className,
}: {
  text: string;
  notExists?: boolean;
  value?: string | ReactElement;
  className?: string;
}) => {
  const currentValue = value ? value : "";
  let styling = {};

  if (notExists) {
    styling = { opacity: "0.7" };
  }

  return (
    <div
      className={cn("mb-2", className, {
        "text-gray-500": notExists,
      })}
      style={styling}
    >
      <span className="font-bold text-gray-600">{text}</span>:{" "}
      {notExists ? "No information" : currentValue}
    </div>
  );
};

const FreeText = ({ title, text }: { title?: string; text?: string }) => {
  return (
    <div className="mb-2 flex flex-wrap">
      {title && (
        <div className="mb-2 shrink-0 pr-2 font-bold text-gray-600">{`${title}:`}</div>
      )}

      {text && <div className="whitespace-pre-line">{text}</div>}
    </div>
  );
};

interface ISelectedBidComponentProps {
  lenderResponse: ILenderResponse;
  lenderApplication: ILenderApplicationExtended;
}

const SelectedBidComponent = ({
  lenderResponse,
  lenderApplication,
}: ISelectedBidComponentProps) => {
  return (
    <LenderResponse
      lenderResponse={lenderResponse}
      lenderApplication={lenderApplication}
      preview={false}
    />
  );
};

const LenderApplicationDetails = () => {
  const navigate = useNavigate();
  const isMobile = useMobile();

  const { lenderCompanyId, id } = useParams();
  const {
    data: lenderApplication,
    isPending,
    isError,
  } = useGetLenderApplicationDetailQuery(Number(lenderCompanyId), Number(id));

  const { data: dataForChart } = useGetAccountingData(
    Number(lenderApplication?.loanApplication.id),
    {
      enabled: !!lenderApplication?.loanApplication.id,
    },
  );
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const { data: me } = useGetMeQuery();

  const countryCode = lenderCompany?.countryCode;
  const setCurrentlyUpdatingLenderResponseId = useLenderApplicationDetailsStore(
    (state) => state.setCurrentlyUpdatingLenderResponseId,
  );

  const currency = lenderApplication?.loanApplication.currency ?? Currency.SEK;
  const [formattedCurrencyValue, calculatedCurrency] = useFormatCurrency({
    currency,
  });

  const methods = useForm({
    mode: "all",
    criteriaMode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(
      bidValidationSchema({
        currency,
        estimatedInvoiceAmount: 0,
        formatWithCurrency: (value) => formattedCurrencyValue(value, currency),
      }),
    ),
    defaultValues: {
      bids: [],
      fieldRequests: [],
      promotions: [],
      creditTermsFile: undefined,
    },
    shouldUnregister: false,
  });

  const fieldRequestObject = useMemo(() => {
    return lenderApplication?.loanApplication.fieldRequests.reduce(
      (finalObj, request) => {
        // prepare an object which will have the fieldRequest field name as key and the whole fieldRequest as value
        finalObj[request.field] = request as never;

        return finalObj;
      },
      {} as FieldRequestObject,
    );
  }, [lenderApplication?.loanApplication.fieldRequests]);

  const realEstateRepaymentChoicesObject = useMemo(() => {
    const groupedRepaymentFR =
      fieldRequestObject?.grouped_real_estate_repayment as
        | IFieldRequest<{
            choices: GroupedFieldRequestSubmittedDataChoice[];
          }>
        | undefined;

    const choices = groupedRepaymentFR?.submittedData.choices;

    if (!choices) {
      return {};
    }

    interface ChoiceType {
      name: string;
      value: string;
      selected: boolean;
    }

    return choices.reduce<Record<string, ChoiceType | undefined>>(
      (finalObj, repaymentChoice: ChoiceType) => {
        finalObj[repaymentChoice.value.toLowerCase()] = repaymentChoice;

        return finalObj;
      },
      {},
    );
  }, [fieldRequestObject?.grouped_real_estate_repayment]);

  if (isPending || isError)
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Preloader className="text-primary" size="xl" />
      </div>
    );

  const selectedLenderResponse =
    lenderApplication.loanApplication.selectedLenderResponse;
  const selectedLenderResponseId = selectedLenderResponse?.id;
  const loanApplication = lenderApplication.loanApplication;
  const lenderApplicationId = lenderApplication.id;
  const contactPerson = loanApplication.contactPerson;
  const applyingCompany = loanApplication.applyingCompany;
  const latestFinancialRecord = applyingCompany.latestFinancialRecord;
  const insights = loanApplication.insights;

  const customerCompany = applyingCompany.customerCompany;

  const companyName = customerCompany.name;
  const companyOrgNumber = customerCompany.organisationNumber;
  const companyType = customerCompany.type;

  const refreshToken = useAuthStore.getState().refreshToken;
  const isOwnApplication = me?.ssn === loanApplication.contactPerson?.ssn;
  const customerAppUrl =
    isOwnApplication && countryCode && refreshToken
      ? `${getAppUrls(loanApplication.countryCode, import.meta.env.MODE as EnvironmentEnum).customer}/current-application/${loanApplication.id}?quickAuth=${refreshToken}`
      : null;

  const dateReceived = formatTime(lenderApplication.markedEligibleForLenderAt);
  const dateUpdated = formatTime(loanApplication.updatedAt);
  const timeToResult = timeTo(loanApplication.biddingEndsAt);
  const dateBiddingEnds = timeToResult.restTime;
  const timeWarning = timeToResult.warning;

  const lenderResponsesWithLoanBids = lenderApplication.lenderResponses?.filter(
    ({ id, bids }) =>
      bids.length === 1 &&
      bids[0]?.type === BidTypeEnum.COMPANY_LOAN &&
      id !== selectedLenderResponseId,
  );

  const lenderResponsesWithFactoringBids =
    lenderApplication.lenderResponses?.filter(
      ({ id, bids }) =>
        bids.length === 1 &&
        bids[0]?.type === BidTypeEnum.FACTORING &&
        id !== selectedLenderResponseId,
    );

  const lenderResponsesWithRealEstateBids =
    lenderApplication.lenderResponses?.filter(
      ({ id, bids }) =>
        bids.length === 1 &&
        bids[0]?.type === BidTypeEnum.REAL_ESTATE &&
        id !== selectedLenderResponseId,
    );

  const lenderResponsesWithLeasingBids =
    lenderApplication.lenderResponses?.filter(
      ({ id, bids }) =>
        bids.length === 1 &&
        bids[0]?.type === BidTypeEnum.LEASING &&
        id !== selectedLenderResponseId,
    );

  const lenderResponsesWithRevolvingCreditBids =
    lenderApplication.lenderResponses?.filter(
      ({ id, bids }) =>
        bids.length === 1 &&
        bids[0]?.type === BidTypeEnum.REVOLVING_CREDIT &&
        id !== selectedLenderResponseId,
    );

  const lenderResponsesWithPurchaseFinancingBids =
    lenderApplication.lenderResponses?.filter(
      ({ id, bids }) =>
        bids.length === 1 &&
        bids[0]?.type === BidTypeEnum.PURCHASE_FINANCING &&
        id !== selectedLenderResponseId,
    );

  const lenderResponsesWithMultipleBids =
    lenderApplication.lenderResponses?.filter(
      ({ id, bids }) => bids.length > 1 && id !== selectedLenderResponseId,
    );

  const placeAnotherBid = () => {
    setCurrentlyUpdatingLenderResponseId(null);
    ScrollToElement("scrollToBidsForm");
  };

  const reasonText =
    loanApplication.reasonDisplayName !== loanApplication.reason
      ? `${loanApplication.reasonDisplayName} (${loanApplication.reason})`
      : loanApplication.reasonDisplayName;

  const signatoriesText = loanApplication.contactPersonHasSigningRightForCompany
    ? "Contact person has signing rights"
    : "Contact person does not have signing rights";

  const beneficialOwnerText = applyingCompany.beneficialOwnerExists
    ? "Beneficial Owner is registered"
    : "Beneficial Owner not registered";

  const groupStructureText = applyingCompany.groupStructureExists
    ? "Group structure exists"
    : "Group structure does not exist";

  const getIsPepTextByCondition = (isPep?: boolean | null) => {
    switch (isPep) {
      case true:
        return "Registered as part of PEP register";
      case false:
        return "Not registered as part of PEP register";
      default:
        return "No information";
    }
  };

  const getTimeFrameInsightDatesPrefix = (data?: IInsightsItem): string => {
    if (!data) {
      return "";
    }
    const toDate = data.toDate;
    const fromDate = data.fromDate;

    return ` (${fromDate} - ${toDate})`;
  };

  return (
    <Form {...methods}>
      <div className="pb-10">
        <div className="mb-4 flex-wrap items-start border-b pb-3 md:items-center md:justify-between">
          <Button
            onClick={() => navigate(-1)}
            variant="secondary"
            className="flex items-center px-4"
          >
            <ArrowLeft className="size-4" />
            <span>Go back</span>
          </Button>
          <div className="-mx-2 mt-4 flex flex-wrap md:mt-0">
            <div className="shrink-0 px-2">
              <ToolbarTime title="Received" time={dateReceived} />
            </div>

            {dayjs(dateUpdated).isAfter(dayjs(dateReceived)) && (
              <div className="shrink-0 px-2">
                <ToolbarTime title="Updated" time={dateUpdated} />
              </div>
            )}

            <div className="shrink-0 px-2">
              <ToolbarTime
                title="Time left"
                time={dateBiddingEnds}
                timeWarning={timeWarning}
              />
            </div>
          </div>
        </div>

        <Toolbar lenderApplication={lenderApplication} />
        <DetailsContainer
          title={
            <div className="flex items-center">
              <span>{`${companyName} (${companyOrgNumber})`}</span>
              {lenderApplication.isHighlightedCustomerSegment && (
                <HighlightedCustomerSegmentIndicator className={"ml-2"} />
              )}
              {customerAppUrl &&
                import.meta.env.MODE !== EnvironmentEnum.PRODUCTION && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="flex items-center gap-1 ml-2"
                    onClick={() =>
                      window.open(
                        customerAppUrl,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                  >
                    <ExternalLink className="size-4" />
                    <span>View as customer</span>
                  </Button>
                )}
            </div>
          }
          status={
            <StatusLabel
              type={lenderApplication.type}
              hasActiveLenderResponses={Boolean(
                lenderApplication.lenderResponses &&
                  lenderApplication.lenderResponses.filter(
                    (response) => response.isActive,
                  ).length > 0,
              )}
            />
          }
        >
          {[
            LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_BID_SELECTED,
            LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_PAYOUT_RECEIVED_BY_CUSTOMER,
          ].includes(lenderApplication.type) ? (
            <>
              {selectedLenderResponse && (
                <div className="-mx-3 flex flex-wrap">
                  <div className="mb-10 mt-5 w-full px-3">
                    <DetailsContainer title="Selected offer">
                      <SelectedBidComponent
                        lenderResponse={selectedLenderResponse}
                        lenderApplication={lenderApplication}
                      />
                    </DetailsContainer>
                  </div>
                </div>
              )}
              <div className="-mx-3 flex flex-wrap">
                <div className="w-full px-3">
                  {lenderApplication.type ===
                  LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_BID_SELECTED ? (
                    <Element name="scrollToNotes">
                      <LenderNotes
                        lenderInternalNotes={
                          lenderApplication.lenderInternalComments
                        }
                        lenderInternalCommentsUpdatedAt={
                          lenderApplication.lenderInternalCommentsUpdatedAt
                        }
                        lenderApplicationId={lenderApplicationId}
                      />
                    </Element>
                  ) : null}
                </div>
              </div>
            </>
          ) : null}
          <div className="-mx-3 flex flex-wrap">
            <div className="mb-5 w-full px-3 xl:w-1/2">
              <DetailsCard title="About company">
                <DetailsItemGrid>
                  <DetailsItemGridData
                    title="Company"
                    value={`${companyName} (${companyOrgNumber})`}
                  />

                  {customerCompany.type && (
                    <DetailsItemGridData
                      title="Company type"
                      value={ucFirst(
                        customerCompany.type
                          .replace(/[_]+/g, " ")
                          .toLowerCase(),
                      )}
                    />
                  )}

                  {applyingCompany.sniCodes &&
                    applyingCompany.sniCodes.length > 0 && (
                      <DetailsItemGridData
                        title="Industry"
                        value={
                          <DetailsItemList>
                            {applyingCompany.sniCodes.map(
                              (item: Record<string, string>, index: number) => (
                                <li
                                  key={index}
                                >{`${item.code} - ${item.description}`}</li>
                              ),
                            )}
                          </DetailsItemList>
                        }
                      />
                    )}

                  {customerCompany.numberEmployeesInterval && (
                    <DetailsItemGridData
                      title="Employees"
                      value={customerCompany.numberEmployeesInterval}
                    />
                  )}

                  {customerCompany.county && (
                    <DetailsItemGridData
                      title="County"
                      value={customerCompany.county}
                    />
                  )}

                  {customerCompany.incorporationDate && (
                    <DetailsItemGridData
                      title="Incorporation"
                      value={customerCompany.incorporationDate}
                    />
                  )}

                  <DetailsItemGridData
                    indicator
                    title="Group structure"
                    value={groupStructureText}
                    isDone={!applyingCompany.groupStructureExists}
                    notExists={applyingCompany.groupStructureExists == null}
                    tooltip={groupStructureText}
                  />
                </DetailsItemGrid>
              </DetailsCard>
            </div>
            <div className="mb-5 w-full px-3 xl:w-1/2">
              <DetailsCard title="Contact person">
                {contactPerson.isPep && (
                  <Alert variant="warning" className="px-3 py-2">
                    Politically exposed person
                  </Alert>
                )}

                <DetailsItemGrid>
                  <DetailsItemGridData
                    title="First name"
                    value={contactPerson.firstName}
                  />

                  <DetailsItemGridData
                    title="Last name"
                    value={contactPerson.lastName}
                  />

                  {contactPerson.ssn && (
                    <DetailsItemGridData
                      title="SSN"
                      value={contactPerson.ssn}
                    />
                  )}

                  {/*
                TODO : Turned off for now, See KREA-2699
                <DetailsItemGridData
                  title="Background check"
                  indicator={getPersonBackground(contactPerson.ssn) !== null}
                  isDone={getPersonBackground(contactPerson.ssn) === 'OK'}
                  value={personBackgroundText(contactPerson.ssn)}
                  notExists={getPersonBackground(contactPerson.ssn) === null}
                  tooltip={personBackgroundText(contactPerson.ssn)}
                /> */}

                  {contactPerson.mobilePhoneNumber && (
                    <DetailsItemGridData
                      title="Mobile phone"
                      value={contactPerson.mobilePhoneNumber}
                    />
                  )}

                  {contactPerson.contactEmail && (
                    <DetailsItemGridData
                      title="Email"
                      value={contactPerson.contactEmail}
                    />
                  )}
                </DetailsItemGrid>
              </DetailsCard>
            </div>

            {applyingCompany.groupStructure && (
              <div className="mb-5 w-full px-3">
                <DetailsCard title="Group structure">
                  <CompanyStructure
                    companyStructure={applyingCompany.groupStructure}
                  />
                </DetailsCard>
              </div>
            )}

            {applyingCompany.beneficialOwners &&
              applyingCompany.beneficialOwners.length > 0 && (
                <div className="mb-5 w-full px-3">
                  <DetailsCard title="Beneficial owners">
                    <Table>
                      <thead className="[&_tr]:border-b">
                        <tr>
                          <th className="h-10 px-2 text-left align-middle font-medium text-gray-600">
                            Name
                          </th>
                          <th className="h-10 px-2 text-left align-middle font-medium text-gray-600">
                            Person Number
                          </th>
                          <th className="h-10 px-2 text-left align-middle font-medium text-gray-600">
                            Extent of Control
                          </th>
                          <th className="h-10 px-2 text-left align-middle font-medium text-gray-600">
                            PEP
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {applyingCompany.beneficialOwners.map(
                          (beneficialOwner, index) => (
                            <tr
                              key={index}
                              className="border-b transition-colors hover:bg-gray-100/50"
                            >
                              <td className="p-2 align-middle">
                                {beneficialOwner.fullName}
                              </td>
                              <td className="p-2 align-middle">
                                {beneficialOwner.ssn}
                              </td>
                              <td className="p-2 align-middle">
                                {beneficialOwner.extentOfControlDescription}
                              </td>
                              <td className="p-2 align-middle">
                                {getIsPepTextByCondition(beneficialOwner.isPep)}
                              </td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </Table>
                  </DetailsCard>
                </div>
              )}

            <div className="mb-5 w-full px-3 xl:w-1/2">
              <DetailsCard title="Loan request details">
                <DetailsItemGrid>
                  <DetailsItemGridData
                    title="Amount"
                    value={formattedCurrencyValue(
                      loanApplication.requestedLoanAmount,
                    )}
                  />

                  <DetailsItemGridData
                    title="Repayment time"
                    value={`${loanApplication.requestedPaybackTimeMonths} ${
                      loanApplication.requestedPaybackTimeMonths > 1
                        ? "months"
                        : "month"
                    }`}
                  />

                  <DetailsItemGridData title="Reason" value={reasonText} />
                </DetailsItemGrid>
              </DetailsCard>
            </div>
            <div className="mb-5 w-full px-3 xl:w-1/2">
              <DetailsCard title="KYC">
                <DetailsItemGrid>
                  <DetailsItemGridData
                    indicator
                    isDone={contactPerson.isPep != null && !contactPerson.isPep}
                    notExists={contactPerson.isPep == null}
                    title="PEP"
                    value={getIsPepTextByCondition(contactPerson.isPep)}
                    tooltip={getIsPepTextByCondition(contactPerson.isPep)}
                  />
                  <DetailsItemGridData
                    indicator
                    isDone={
                      loanApplication.contactPersonHasSigningRightForCompany !=
                        null &&
                      loanApplication.contactPersonHasSigningRightForCompany
                    }
                    notExists={
                      loanApplication.contactPersonHasSigningRightForCompany ==
                      null
                    }
                    title="Signatories"
                    value={signatoriesText}
                    tooltip={signatoriesText}
                  />

                  {customerCompany.type !== "SOLE_TRADER" && (
                    <DetailsItemGridData
                      indicator
                      title="Beneficial Owner"
                      isDone={!!applyingCompany.beneficialOwnerExists}
                      notExists={applyingCompany.beneficialOwnerExists == null}
                      value={beneficialOwnerText}
                      tooltip={beneficialOwnerText}
                    />
                  )}
                </DetailsItemGrid>
              </DetailsCard>
            </div>

            {fieldRequestObject?.leasing?.isSubmitted && (
              <div className="mb-5 w-full px-3">
                <DetailsCard title="Leasing">
                  <DetailsItemGrid>
                    {fieldRequestObject.leasing.submittedData.leasingAmount && (
                      <DetailsItemGridData
                        title="Leasing amount"
                        value={formattedCurrencyValue(
                          fieldRequestObject.leasing.submittedData
                            .leasingAmount,
                        )}
                      />
                    )}

                    <DetailsItemGridData
                      title="Leasing object"
                      value={fieldRequestObject.leasing.submittedData.leasingProductType?.replace(
                        /_/g,
                        " ",
                      )}
                    />

                    {fieldRequestObject.leasing_additional_data?.submittedData
                      .leasingProductDescription && (
                      <DetailsItemGridData
                        title="Description"
                        value={
                          <>
                            <FreeText
                              text={
                                fieldRequestObject.leasing_additional_data
                                  .submittedData.leasingProductDescription
                              }
                            />
                          </>
                        }
                      />
                    )}

                    {fieldRequestObject.leasing_additional_data?.submittedData
                      .files &&
                      !isEmpty(
                        fieldRequestObject.leasing_additional_data.submittedData
                          .files,
                      ) &&
                      fieldRequestObject.leasing_additional_data.submittedData
                        .files.length > 0 && (
                        <DetailsItemGridData
                          title="Files"
                          value={
                            <DetailsItemList>
                              {fieldRequestObject.leasing_additional_data.submittedData.files.map(
                                (item) => (
                                  <li key={item.fileId}>
                                    <AttachedFile
                                      title={item.originalFileName}
                                      url={item.url}
                                    />
                                  </li>
                                ),
                              )}
                            </DetailsItemList>
                          }
                        />
                      )}

                    {fieldRequestObject.leasing_additional_data?.submittedData
                      .leasingProductUrl && (
                      <DetailsItemGridData
                        title="URL link to the object"
                        value={
                          <AttachedLink
                            value={
                              fieldRequestObject.leasing_additional_data
                                .submittedData.leasingProductUrl
                            }
                          />
                        }
                      />
                    )}
                  </DetailsItemGrid>
                </DetailsCard>
              </div>
            )}

            <CreditCaseFileBlock
              creditCaseFileUrl={
                applyingCompany.creditCaseFile?.fileUrl ?? null
              }
              creditCaseFileTitle={
                applyingCompany.creditCaseFile?.originalFileName ?? null
              }
            />
            {loanApplication.purposeFreeTextAnswer && (
              <div className="mb-5 w-full px-3">
                <DetailsCard title="Additional information about purpose">
                  <DetailsItemList>
                    <li>{loanApplication.purposeFreeTextAnswer}</li>
                  </DetailsItemList>
                </DetailsCard>
              </div>
            )}
            {!isEmpty(
              fieldRequestObject?.refinancing_amounts?.submittedData
                .refinancingAmounts,
            ) && (
              <div className="mb-5 w-full px-3">
                <DetailsCard title="Existing financing">
                  <Table>
                    <thead className="[&_tr]:border-b">
                      <tr>
                        <th className="h-10 px-2 text-left align-middle font-medium text-gray-600">
                          Lender Name
                        </th>
                        <th className="h-10 px-2 text-left align-middle font-medium text-gray-600">
                          Outstanding Amount
                        </th>
                        <th className="h-10 px-2 text-left align-middle font-medium text-gray-600">
                          Monthly Cost
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {fieldRequestObject?.refinancing_amounts?.submittedData.refinancingAmounts.map(
                        (item, index) => (
                          <tr
                            key={`${index}refinancing_amounts`}
                            className="border-b transition-colors hover:bg-gray-100/50"
                          >
                            <td className="p-2 align-middle">
                              {item.lenderName}
                            </td>
                            <td className="p-2 align-middle">
                              {item.outstandingAmount}
                            </td>
                            <td className="p-2 align-middle">
                              {item.monthlyCost}
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </Table>
                  {!isEmpty(
                    fieldRequestObject?.refinancing_amounts?.submittedData
                      .files,
                  ) && (
                    <>
                      <TextItem text="Uploaded files" className="mb-2" />
                      <DetailsItemList>
                        {fieldRequestObject?.refinancing_amounts?.submittedData.files.map(
                          (item) => (
                            <li key={item.fileId}>
                              <AttachedFile
                                title={item.originalFileName}
                                url={item.url}
                              />
                            </li>
                          ),
                        )}
                      </DetailsItemList>
                    </>
                  )}
                </DetailsCard>
              </div>
            )}
            {applyingCompany.boardMembers &&
              applyingCompany.boardMembers.length > 0 && (
                <div className="mb-5 w-full px-3">
                  <DetailsCard title="Board members">
                    <Table>
                      <thead className="[&_tr]:border-b">
                        <tr>
                          <th className="h-10 px-2 text-left align-middle font-medium text-gray-600">
                            Name
                          </th>
                          <th className="h-10 px-2 text-left align-middle font-medium text-gray-600">
                            Role
                          </th>
                          <th className="h-10 px-2 text-left align-middle font-medium text-gray-600">
                            Person Number
                          </th>
                          <th className="h-10 px-2 text-left align-middle font-medium text-gray-600">
                            Joined at
                          </th>
                          {/*
                        TODO : Turned off for now, See KREA-2699
                        <th>Background check</th>
                       */}
                        </tr>
                      </thead>
                      <tbody>
                        {applyingCompany.boardMembers.map((item, index) => (
                          <tr
                            key={index}
                            className="border-b transition-colors hover:bg-gray-100/50"
                          >
                            <td className="p-2 align-middle">
                              {!item.firstName && !item.lastName
                                ? "No name registered"
                                : `${item.firstName ? item.firstName : ""} ${item.lastName ? item.lastName : ""}`}
                            </td>
                            <td className="p-2 align-middle">
                              {item.roles && item.roles.length > 0
                                ? item.roles.map((itm, indx) => (
                                    <div
                                      key={indx}
                                    >{`${itm.roleName} (${itm.roleCode})`}</div>
                                  ))
                                : "-"}
                            </td>
                            <td className="p-2 align-middle">{item.ssn}</td>
                            <td className="p-2 align-middle">
                              {item.roles && item.roles.length > 0
                                ? item.roles.map((itm, indx) => (
                                    <div key={indx}>{itm.fromDate}</div>
                                  ))
                                : "-"}
                            </td>
                            {/* TODO : Turned off for now, See KREA-2699 */}
                            {/* <td>{personBackgroundText(item.ssn)}</td> */}
                            {/* TODO : Turned off for now, See KREA-2699 */}
                            {/* <td className="text-right">
                          {getPersonBackground(item.ssn) !== null && (
                            <div className="d-inline-block">
                              <DataStatusIndicator
                                isDone={getPersonBackground(item.ssn) === 'OK'}
                                tooltip={personBackgroundText(item.ssn)}
                              />
                            </div>
                          )}
                        </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </DetailsCard>
                </div>
              )}

            <div className="mb-5 w-full px-3">
              <DetailsCard title="Repayment capacity">
                <DetailsItemGrid>
                  {countryCode === CountryCode.FI &&
                  companyType === "SOLE_TRADER" ? null : (
                    <DetailsItemGridData
                      title="Credit score"
                      value={
                        <div className="flex h-full items-center">
                          <Indicator risk={applyingCompany.creditScoreColor} />
                        </div>
                      }
                    />
                  )}

                  <DetailsItemGridData
                    notExists={
                      !fieldRequestObject?.self_estimated_historic_revenue
                        ?.isSubmitted &&
                      !fieldRequestObject?.self_estimated_revenue?.isSubmitted
                    }
                    title="Customer estimated revenues"
                    value={
                      <DetailsItemList>
                        <li>
                          <TextItem
                            text="Last 12 months"
                            notExists={
                              !fieldRequestObject
                                ?.self_estimated_historic_revenue?.submittedData
                            }
                            value={formattedCurrencyValue(
                              fieldRequestObject
                                ?.self_estimated_historic_revenue?.submittedData
                                .selfEstimatedHistoricRevenue,
                            )}
                          />
                        </li>

                        <li>
                          <TextItem
                            text="Next 12 months"
                            notExists={
                              !fieldRequestObject?.self_estimated_revenue
                                ?.submittedData.selfEstimatedRevenue
                            }
                            value={formattedCurrencyValue(
                              fieldRequestObject?.self_estimated_revenue
                                ?.submittedData.selfEstimatedRevenue,
                            )}
                          />
                        </li>
                      </DetailsItemList>
                    }
                  />

                  <DetailsItemGridData
                    notExists={
                      !insights?.revenueLast12Months &&
                      !insights?.revenueSinceFinancialReport
                    }
                    title="Computed estimated revenues"
                    value={
                      <DetailsItemList>
                        {insights?.revenueLast12Months && (
                          <li>
                            <TextItem
                              text="Last 12 months"
                              notExists={!insights.revenueLast12Months}
                              value={
                                <span>
                                  {formattedCurrencyValue(
                                    insights.revenueLast12Months.value,
                                  )}
                                  <span className="text-[15px] text-gray-600">
                                    {getTimeFrameInsightDatesPrefix(
                                      insights.revenueLast12Months,
                                    )}
                                  </span>
                                </span>
                              }
                            />
                          </li>
                        )}

                        {insights?.revenueSinceFinancialReport && (
                          <li>
                            <TextItem
                              text="Since latest financial report"
                              notExists={!insights.revenueSinceFinancialReport}
                              value={
                                <span>
                                  {formattedCurrencyValue(
                                    insights.revenueSinceFinancialReport.value,
                                  )}
                                  <span className="text-[15px] text-gray-600">
                                    {getTimeFrameInsightDatesPrefix(
                                      insights.revenueSinceFinancialReport,
                                    )}
                                  </span>
                                </span>
                              }
                            />
                          </li>
                        )}
                      </DetailsItemList>
                    }
                  />

                  {dataForChart && !isEmpty(dataForChart.profitLossEntries) && (
                    <DetailsItemGridData
                      title="Last FY data"
                      value={
                        <AccountingDataChart
                          profitLossEntries={dataForChart.profitLossEntries}
                          currency={calculatedCurrency}
                        />
                      }
                    />
                  )}
                  {!isEmpty(
                    fieldRequestObject?.accounting_data?.submittedData.files,
                  ) && (
                    <DetailsItemGridData
                      title="Accounting data"
                      value={
                        <DetailsItemList>
                          {fieldRequestObject?.accounting_data?.submittedData &&
                            !isEmpty(
                              fieldRequestObject.accounting_data.submittedData
                                .files,
                            ) && (
                              <li>
                                <TextItem
                                  text="Uploaded files"
                                  notExists={isEmpty(
                                    fieldRequestObject.accounting_data
                                      .submittedData.files,
                                  )}
                                  className="mb-2"
                                />

                                <DetailsItemList>
                                  {fieldRequestObject.accounting_data.submittedData.files.map(
                                    (item) => (
                                      <li key={item.fileId}>
                                        <AttachedFile
                                          title={item.originalFileName}
                                          url={item.url}
                                        />
                                      </li>
                                    ),
                                  )}
                                </DetailsItemList>
                              </li>
                            )}
                        </DetailsItemList>
                      }
                    />
                  )}
                  {(!isEmpty(
                    fieldRequestObject?.annual_report?.submittedData.files,
                  ) ||
                    !isEmpty(dataForChart?.profitLossEntries)) && (
                    <DetailsItemGridData
                      title="Financial report"
                      value={
                        <DetailsItemList>
                          {fieldRequestObject?.annual_report?.submittedData &&
                            !isEmpty(
                              fieldRequestObject.annual_report.submittedData
                                .files,
                            ) && (
                              <li>
                                <TextItem
                                  text="Uploaded files"
                                  notExists={isEmpty(
                                    fieldRequestObject.annual_report
                                      .submittedData.files,
                                  )}
                                  className="mb-2"
                                />

                                <DetailsItemList>
                                  {fieldRequestObject.annual_report.submittedData.files.map(
                                    (item) => (
                                      <li key={item.fileId}>
                                        <AttachedFile
                                          title={item.originalFileName}
                                          url={item.url}
                                        />
                                      </li>
                                    ),
                                  )}
                                </DetailsItemList>
                              </li>
                            )}
                          {dataForChart?.profitLossEntries &&
                            dataForChart.profitLossEntries.length > 0 && (
                              <li>
                                <StructuredData title="Financial information available">
                                  <Link
                                    to={`/lender-company/${lenderCompanyId}/lender-application/${id}/accounting-data`}
                                  >
                                    View
                                  </Link>
                                </StructuredData>
                              </li>
                            )}
                        </DetailsItemList>
                      }
                    />
                  )}
                  {fieldRequestObject?.banking_data?.isSubmitted && (
                    <DetailsItemGridData
                      title="Banking data"
                      value={
                        <DetailsItemList>
                          {!isEmpty(
                            fieldRequestObject.banking_data.submittedData.files,
                          ) && (
                            <li>
                              <TextItem
                                text="Uploaded files"
                                notExists={isEmpty(
                                  fieldRequestObject.banking_data.submittedData
                                    .files,
                                )}
                                className="mb-2"
                              />

                              <DetailsItemList>
                                {fieldRequestObject.banking_data.submittedData.files.map(
                                  (item) => (
                                    <li key={item.fileId}>
                                      <AttachedFile
                                        title={item.originalFileName}
                                        url={item.url}
                                      />
                                    </li>
                                  ),
                                )}
                              </DetailsItemList>
                            </li>
                          )}

                          {fieldRequestObject.banking_data.submittedData
                            .hasIntegrationData &&
                            applyingCompany.id && (
                              <div className="mb-2">
                                <BankingDataChart
                                  resourceId={Number(applyingCompany.id)}
                                  resourceType={"APPLYING_COMPANY"}
                                  primaryCurrency={loanApplication.currency}
                                  isMobile={isMobile}
                                />
                              </div>
                            )}
                        </DetailsItemList>
                      }
                    />
                  )}
                </DetailsItemGrid>
              </DetailsCard>
            </div>

            <div className="mb-5 w-full px-3">
              <DetailsCard title="Collateral">
                {latestFinancialRecord?.financialYearEnd && (
                  <p className="-mt-3 mb-4 p-0 font-bold text-gray-400">
                    (As of{" "}
                    {dayjs(latestFinancialRecord.financialYearEnd).format(
                      "YYYY-MM-DD",
                    )}
                    )
                  </p>
                )}
                <DetailsItemGrid>
                  <DetailsItemGridData
                    title="Guarantors"
                    value={
                      fieldRequestObject?.guarantors?.isSubmitted ? (
                        <DetailsItemList>
                          {fieldRequestObject.guarantors.submittedData.guarantors.map(
                            (guarantor, index) => (
                              <li key={index} className={cn("mb-4")}>
                                <div>
                                  <span className="font-bold">
                                    {`${guarantor.ssn} ${guarantor.firstName ? guarantor.firstName : ""} ${
                                      guarantor.lastName
                                        ? guarantor.lastName
                                        : ""
                                    }`}
                                  </span>
                                  {` ${guarantor.email ? `- ${guarantor.email}` : ""}`}
                                  {` ${guarantor.mobilePhoneNumber ? `- ${guarantor.mobilePhoneNumber}` : ""}`}
                                </div>
                                {guarantor.bankingData
                                  ?.estimatedMonthlyIncome ? (
                                  <ResponsiveTooltip
                                    trigger={
                                      <div className="mb-2 inline-block">
                                        Estimated monthly income:{" "}
                                        {formattedCurrencyValue(
                                          guarantor.bankingData
                                            .estimatedMonthlyIncome,
                                        )}
                                      </div>
                                    }
                                  >
                                    Only for full months
                                  </ResponsiveTooltip>
                                ) : null}
                                <div className="mb-2">
                                  <BankingDataChart
                                    resourceId={Number(guarantor.id)}
                                    resourceType={"GUARANTOR"}
                                    primaryCurrency={loanApplication.currency}
                                    isMobile={isMobile}
                                  />
                                </div>
                              </li>
                            ),
                          )}
                        </DetailsItemList>
                      ) : (
                        <span className="font-bold text-red-500">
                          No guarantors
                        </span>
                      )
                    }
                  />

                  {latestFinancialRecord && (
                    <DetailsItemGridData
                      title="Assets"
                      value={
                        <DetailsItemList>
                          {(latestFinancialRecord.accountsReceivables ?? 0) >
                            0 && (
                            <li>{`Accounts receivables: ${formattedCurrencyValue(
                              latestFinancialRecord.accountsReceivables,
                            )}`}</li>
                          )}

                          {(latestFinancialRecord.otherCurrentAssets ?? 0) >
                            0 && (
                            <li>{`Other current assets: ${formattedCurrencyValue(
                              latestFinancialRecord.otherCurrentAssets,
                            )}`}</li>
                          )}

                          {(latestFinancialRecord.inventories ?? 0) > 0 && (
                            <li>{`Inventories: ${formattedCurrencyValue(
                              latestFinancialRecord.inventories,
                            )}`}</li>
                          )}

                          {(latestFinancialRecord.equipmentAndMachines ?? 0) >
                            0 && (
                            <li>{`Equipment & machines: ${formattedCurrencyValue(
                              latestFinancialRecord.equipmentAndMachines,
                            )}`}</li>
                          )}
                          {(latestFinancialRecord.landAndBuilding ?? 0) > 0 && (
                            <li>{`Land & building: ${formattedCurrencyValue(
                              latestFinancialRecord.landAndBuilding,
                            )}`}</li>
                          )}
                          {(latestFinancialRecord.financialAssets ?? 0) > 0 && (
                            <li>{`Financial assets: ${formattedCurrencyValue(
                              latestFinancialRecord.financialAssets,
                            )}`}</li>
                          )}
                        </DetailsItemList>
                      }
                    />
                  )}
                </DetailsItemGrid>
              </DetailsCard>
            </div>

            {(fieldRequestObject?.additional_securities?.isSubmitted ||
              fieldRequestObject?.budget?.isSubmitted) && (
              <div className="mb-5 w-full px-3">
                <DetailsCard title="Other information">
                  <DetailsItemGrid>
                    {fieldRequestObject.additional_securities?.submittedData
                      .additionalSecuritiesText && (
                      <DetailsItemGridData
                        notExists={
                          !fieldRequestObject.additional_securities
                            .submittedData.additionalSecuritiesText
                        }
                        title="Comment"
                        value={
                          <FreeText
                            text={
                              fieldRequestObject.additional_securities
                                .submittedData.additionalSecuritiesText
                            }
                          />
                        }
                      />
                    )}

                    {!isEmpty(
                      fieldRequestObject.additional_securities?.submittedData
                        .files,
                    ) && (
                      <DetailsItemGridData
                        title="Uploaded files"
                        value={
                          <DetailsItemList className="-mt-1 mb-0">
                            {fieldRequestObject.additional_securities?.submittedData.files.map(
                              (item) => (
                                <li key={item.fileId}>
                                  <AttachedFile
                                    title={item.originalFileName}
                                    url={item.url}
                                  />
                                </li>
                              ),
                            )}
                          </DetailsItemList>
                        }
                      />
                    )}

                    {fieldRequestObject.budget?.isSubmitted && (
                      <DetailsItemGridData
                        title="Budget"
                        value={
                          <DetailsItemList>
                            {!isEmpty(
                              fieldRequestObject.budget.submittedData.files,
                            ) &&
                              fieldRequestObject.budget.submittedData.files.map(
                                (item) => (
                                  <li key={item.fileId}>
                                    <AttachedFile
                                      title={item.originalFileName}
                                      url={item.url}
                                    />
                                  </li>
                                ),
                              )}
                          </DetailsItemList>
                        }
                      />
                    )}
                  </DetailsItemGrid>
                </DetailsCard>
              </div>
            )}

            {!!latestFinancialRecord && (
              <div className="mb-5 w-full px-3">
                <DetailsCard title="Company mortgage as collateral">
                  {latestFinancialRecord.financialYearEnd && (
                    <p className="-mt-3 mb-4 p-0 font-bold text-gray-400">
                      (As of{" "}
                      {dayjs(latestFinancialRecord.financialYearEnd).format(
                        "YYYY-MM-DD",
                      )}
                      )
                    </p>
                  )}
                  <DetailsItemGrid className="mt-5">
                    {latestFinancialRecord.movableAssets !== null && (
                      <DetailsItemGridData
                        title="Movable assets"
                        value={formattedCurrencyValue(
                          latestFinancialRecord.movableAssets,
                        )}
                      />
                    )}
                  </DetailsItemGrid>
                </DetailsCard>
              </div>
            )}

            {(fieldRequestObject?.property?.submittedData
              .propertyUnitDesignation ||
              fieldRequestObject?.property_additional_data?.submittedData
                .propertyValuation ||
              fieldRequestObject?.parent_company_guarantee?.isSubmitted ||
              fieldRequestObject?.share_mortgage?.isSubmitted ||
              fieldRequestObject?.building_permit?.isSubmitted ||
              fieldRequestObject?.construction_estimate?.isSubmitted) && (
              <div className="mb-5 w-full px-3">
                <DetailsCard title="Property as collateral">
                  <DetailsItemGrid>
                    <DetailsItemGridData
                      title="Real estate mortgage"
                      value={
                        <DetailsItemList>
                          {fieldRequestObject.property?.submittedData
                            .propertyUnitDesignation && (
                            <li>{`Property unit designation: ${fieldRequestObject.property.submittedData.propertyUnitDesignation} `}</li>
                          )}

                          {fieldRequestObject.property_additional_data
                            ?.submittedData.propertyValuation && (
                            <li>{`Property valuation: ${formattedCurrencyValue(fieldRequestObject.property_additional_data.submittedData.propertyValuation)}`}</li>
                          )}

                          {fieldRequestObject.property_additional_data
                            ?.submittedData.propertyUrl && (
                            <li>{`Property url: ${fieldRequestObject.property_additional_data.submittedData.propertyUrl}`}</li>
                          )}

                          {!isEmpty(
                            fieldRequestObject.property_additional_data
                              ?.submittedData.files,
                          ) && (
                            <li>
                              <TextItem
                                text="Uploaded files"
                                notExists={isEmpty(
                                  fieldRequestObject.property_additional_data
                                    ?.submittedData.files,
                                )}
                                className="mb-2"
                              />
                              <DetailsItemList>
                                {!isEmpty(
                                  fieldRequestObject.property_additional_data
                                    ?.submittedData.files,
                                ) &&
                                  fieldRequestObject.property_additional_data?.submittedData.files.map(
                                    (item) => (
                                      <li key={item.fileId}>
                                        <AttachedFile
                                          title={item.originalFileName}
                                          url={item.url}
                                        />
                                      </li>
                                    ),
                                  )}
                              </DetailsItemList>
                            </li>
                          )}
                        </DetailsItemList>
                      }
                    />

                    {(fieldRequestObject.parent_company_guarantee
                      ?.isSubmitted ||
                      fieldRequestObject.share_mortgage?.isSubmitted ||
                      fieldRequestObject.building_permit?.isSubmitted ||
                      fieldRequestObject.construction_estimate
                        ?.isSubmitted) && (
                      <DetailsItemGridData
                        title="Supporting collateral"
                        value={
                          <DetailsItemList>
                            {fieldRequestObject.parent_company_guarantee
                              ?.isSubmitted && (
                              <li>
                                <TextItem
                                  text="Parent company guarantee"
                                  notExists={
                                    !fieldRequestObject.parent_company_guarantee
                                      .isSubmitted
                                  }
                                />

                                <DetailsItemList>
                                  <li>
                                    <TextItem
                                      text="Uploaded files"
                                      notExists={isEmpty(
                                        fieldRequestObject
                                          .parent_company_guarantee
                                          .submittedData.files,
                                      )}
                                      className="mb-2 mt-3"
                                    />
                                    <DetailsItemList>
                                      {!isEmpty(
                                        fieldRequestObject
                                          .parent_company_guarantee
                                          .submittedData.files,
                                      ) &&
                                        fieldRequestObject.parent_company_guarantee.submittedData.files.map(
                                          (item) => (
                                            <li key={item.fileId}>
                                              <AttachedFile
                                                title={item.originalFileName}
                                                url={item.url}
                                              />
                                            </li>
                                          ),
                                        )}
                                    </DetailsItemList>
                                  </li>
                                </DetailsItemList>
                              </li>
                            )}

                            {fieldRequestObject.share_mortgage?.isSubmitted && (
                              <li>
                                <TextItem
                                  text="Share mortgage"
                                  notExists={
                                    !fieldRequestObject.share_mortgage
                                      .isSubmitted
                                  }
                                />

                                <DetailsItemList className="mt-2">
                                  {fieldRequestObject.share_mortgage
                                    .submittedData.shareMortgageText && (
                                    <li>
                                      <FreeText
                                        title="Free text by customer"
                                        text={
                                          fieldRequestObject.share_mortgage
                                            .submittedData.shareMortgageText
                                        }
                                      />
                                    </li>
                                  )}
                                </DetailsItemList>
                              </li>
                            )}

                            {fieldRequestObject.building_permit
                              ?.isSubmitted && (
                              <li>
                                <TextItem
                                  text="Building permit"
                                  notExists={
                                    !fieldRequestObject.building_permit
                                      .isSubmitted
                                  }
                                />

                                <DetailsItemList>
                                  <li>
                                    <TextItem
                                      text="Uploaded files"
                                      notExists={isEmpty(
                                        fieldRequestObject.building_permit
                                          .submittedData.files,
                                      )}
                                    />
                                    <DetailsItemList className="mt-2">
                                      {!isEmpty(
                                        fieldRequestObject.building_permit
                                          .submittedData.files,
                                      ) &&
                                        fieldRequestObject.building_permit.submittedData.files.map(
                                          (item) => (
                                            <li key={item.fileId}>
                                              <AttachedFile
                                                title={item.originalFileName}
                                                url={item.url}
                                              />
                                            </li>
                                          ),
                                        )}
                                    </DetailsItemList>
                                  </li>
                                </DetailsItemList>
                              </li>
                            )}

                            {fieldRequestObject.construction_estimate
                              ?.isSubmitted && (
                              <li>
                                <TextItem
                                  text="Construction estimate"
                                  notExists={
                                    !fieldRequestObject.construction_estimate
                                      .isSubmitted
                                  }
                                />

                                <DetailsItemList>
                                  <li>
                                    <TextItem
                                      text="Uploaded files"
                                      notExists={isEmpty(
                                        fieldRequestObject.construction_estimate
                                          .submittedData.files,
                                      )}
                                    />
                                    <DetailsItemList className="mt-2">
                                      {!isEmpty(
                                        fieldRequestObject.construction_estimate
                                          .submittedData.files,
                                      ) &&
                                        fieldRequestObject.construction_estimate.submittedData.files.map(
                                          (item) => (
                                            <li key={item.fileId}>
                                              <AttachedFile
                                                title={item.originalFileName}
                                                url={item.url}
                                              />
                                            </li>
                                          ),
                                        )}
                                    </DetailsItemList>
                                  </li>
                                </DetailsItemList>
                              </li>
                            )}
                          </DetailsItemList>
                        }
                      />
                    )}
                  </DetailsItemGrid>
                </DetailsCard>
              </div>
            )}

            {loanApplication.eligibleForInvoicePurchaseBids && (
              <div className="mb-5 w-full px-3">
                <DetailsCard title="Invoices as collateral">
                  <DetailsItemGrid>
                    <DetailsItemGridData
                      title="Monthly amount"
                      value={formattedCurrencyValue(
                        applyingCompany.estimatedInvoiceAmount,
                      )}
                    />

                    <DetailsItemGridData
                      title="Number of invoices"
                      value={applyingCompany.numberOfInvoices}
                    />

                    <DetailsItemGridData title="Type" value={"Invoices"} />

                    <DetailsItemGridData
                      title="Average due days"
                      value={applyingCompany.averagePaymentTimeDays}
                    />

                    {applyingCompany.invoicesDescription && (
                      <DetailsItemGridData
                        title="Description"
                        value={
                          <FreeText
                            text={applyingCompany.invoicesDescription}
                          />
                        }
                      />
                    )}

                    {!isEmpty(
                      fieldRequestObject?.unpaid_invoices_additional_data
                        ?.submittedData.files,
                    ) && (
                      <DetailsItemGridData
                        title="Uploaded files"
                        value={
                          <DetailsItemList>
                            {!isEmpty(
                              fieldRequestObject
                                ?.unpaid_invoices_additional_data?.submittedData
                                .files,
                            ) &&
                              fieldRequestObject?.unpaid_invoices_additional_data?.submittedData.files.map(
                                (item) => (
                                  <li key={item.fileId}>
                                    <AttachedFile
                                      title={item.originalFileName}
                                      url={item.url}
                                    />
                                  </li>
                                ),
                              )}
                          </DetailsItemList>
                        }
                      />
                    )}
                  </DetailsItemGrid>
                </DetailsCard>
              </div>
            )}

            {loanApplication.eligibleForRealEstateBids && (
              <div className="mb-5 w-full px-3">
                <DetailsCard title="Repayment of loan (real estate applications only)">
                  <DetailsItemList>
                    <li>
                      <TextItem
                        text="By refinancing the loan"
                        value={
                          realEstateRepaymentChoicesObject.refinancing
                            ?.selected ||
                          fieldRequestObject?.refinancing?.isSubmitted
                            ? "Yes"
                            : ""
                        }
                        notExists={
                          !realEstateRepaymentChoicesObject.refinancing
                            ?.selected &&
                          !fieldRequestObject?.refinancing?.isSubmitted
                        }
                      />
                      {fieldRequestObject?.refinancing?.isSubmitted && (
                        <DetailsItemList className="mt-2">
                          {fieldRequestObject.refinancing.submittedData
                            .refinancingText && (
                            <li>
                              <FreeText
                                title="Free text by customer"
                                text={
                                  fieldRequestObject.refinancing.submittedData
                                    .refinancingText
                                }
                              />
                            </li>
                          )}
                        </DetailsItemList>
                      )}
                    </li>
                    <li>
                      <TextItem
                        text="By sale of property"
                        value={
                          realEstateRepaymentChoicesObject.sale_of_property
                            ?.selected ||
                          fieldRequestObject?.sale_of_property?.isSubmitted
                            ? "Yes"
                            : ""
                        }
                        notExists={
                          !realEstateRepaymentChoicesObject.sale_of_property
                            ?.selected &&
                          !fieldRequestObject?.sale_of_property?.isSubmitted
                        }
                      />
                      {fieldRequestObject?.sale_of_property?.isSubmitted && (
                        <DetailsItemList className="mt-2">
                          {fieldRequestObject.sale_of_property.submittedData
                            .saleOfPropertyText && (
                            <li>
                              <FreeText
                                title="Free text by customer"
                                text={
                                  fieldRequestObject.sale_of_property
                                    .submittedData.saleOfPropertyText
                                }
                              />
                            </li>
                          )}
                        </DetailsItemList>
                      )}
                    </li>
                    <li>
                      <TextItem
                        text="By cashflow from the property"
                        value={
                          realEstateRepaymentChoicesObject.property_revenue
                            ?.selected ||
                          fieldRequestObject?.property_cash_flow?.isSubmitted
                            ? "Yes"
                            : ""
                        }
                        notExists={
                          !realEstateRepaymentChoicesObject.property_revenue
                            ?.selected &&
                          !fieldRequestObject?.property_cash_flow?.isSubmitted
                        }
                      />
                      {fieldRequestObject?.property_cash_flow?.isSubmitted && (
                        <DetailsItemList className="mt-2">
                          <li>
                            <TextItem
                              text="Uploaded files"
                              notExists={isEmpty(
                                fieldRequestObject.property_cash_flow
                                  .submittedData.files,
                              )}
                              className="mb-2"
                            />
                            <DetailsItemList>
                              {!isEmpty(
                                fieldRequestObject.property_cash_flow
                                  .submittedData.files,
                              ) &&
                                fieldRequestObject.property_cash_flow.submittedData.files.map(
                                  (item) => (
                                    <li key={item.fileId}>
                                      <AttachedFile
                                        title={item.originalFileName}
                                        url={item.url}
                                      />
                                    </li>
                                  ),
                                )}
                            </DetailsItemList>
                          </li>
                        </DetailsItemList>
                      )}
                    </li>
                    {isBoolean(
                      realEstateRepaymentChoicesObject.company_revenue
                        ?.selected,
                    ) && (
                      <li>
                        <TextItem
                          text="By cashflow from the company"
                          value={
                            realEstateRepaymentChoicesObject.company_revenue
                              .selected
                              ? "Yes"
                              : ""
                          }
                          notExists={
                            !realEstateRepaymentChoicesObject.company_revenue
                              .selected
                          }
                        />
                        {realEstateRepaymentChoicesObject.company_revenue
                          .selected && (
                          <DetailsItemList className="mt-2">
                            {fieldRequestObject?.accounting_data
                              ?.submittedData &&
                              !isEmpty(
                                fieldRequestObject.accounting_data.submittedData
                                  .files,
                              ) && (
                                <li>
                                  <TextItem
                                    text="Uploaded files"
                                    notExists={isEmpty(
                                      fieldRequestObject.accounting_data
                                        .submittedData.files,
                                    )}
                                    className="mb-2"
                                  />

                                  <DetailsItemList>
                                    {fieldRequestObject.accounting_data.submittedData.files.map(
                                      (item) => (
                                        <li key={item.fileId}>
                                          <AttachedFile
                                            title={item.originalFileName}
                                            url={item.url}
                                          />
                                        </li>
                                      ),
                                    )}
                                  </DetailsItemList>
                                </li>
                              )}
                          </DetailsItemList>
                        )}
                      </li>
                    )}
                    {(realEstateRepaymentChoicesObject.construction_completed
                      ?.selected ||
                      fieldRequestObject?.construction_completed_repayment
                        ?.isSubmitted) && (
                      <li>
                        <TextItem
                          text="Repayment after the construction is complete"
                          value={
                            realEstateRepaymentChoicesObject
                              .construction_completed?.selected ||
                            fieldRequestObject?.construction_completed_repayment
                              ?.isSubmitted
                              ? "Yes"
                              : ""
                          }
                          notExists={
                            !realEstateRepaymentChoicesObject
                              .construction_completed?.selected &&
                            !fieldRequestObject
                              ?.construction_completed_repayment?.isSubmitted
                          }
                        />
                        {fieldRequestObject?.construction_completed_repayment
                          ?.isSubmitted && (
                          <DetailsItemList className="mt-2">
                            {fieldRequestObject.construction_completed_repayment
                              .submittedData
                              .constructionCompletedRepaymentText && (
                              <li>
                                {`Free text by customer: ${fieldRequestObject.construction_completed_repayment.submittedData.constructionCompletedRepaymentText} `}
                              </li>
                            )}
                          </DetailsItemList>
                        )}
                      </li>
                    )}
                  </DetailsItemList>
                </DetailsCard>
              </div>
            )}
          </div>
        </DetailsContainer>
        {lenderApplication.type !==
        LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_BID_SELECTED ? (
          <Element name="scrollToNotes">
            <LenderNotes
              lenderInternalNotes={lenderApplication.lenderInternalComments}
              lenderInternalCommentsUpdatedAt={
                lenderApplication.lenderInternalCommentsUpdatedAt
              }
              lenderApplicationId={lenderApplication.id}
            />
          </Element>
        ) : null}
        {!isEmpty(lenderResponsesWithLoanBids) && (
          <Bids
            lenderResponses={lenderResponsesWithLoanBids ?? []}
            title="Your loan offers"
            lenderApplication={lenderApplication}
          />
        )}
        {!isEmpty(lenderResponsesWithFactoringBids) && (
          <Bids
            lenderResponses={lenderResponsesWithFactoringBids ?? []}
            title="Your factoring offers"
            lenderApplication={lenderApplication}
          />
        )}
        {!isEmpty(lenderResponsesWithRealEstateBids) && (
          <Bids
            lenderResponses={lenderResponsesWithRealEstateBids ?? []}
            title="Your real estate offers"
            lenderApplication={lenderApplication}
          />
        )}
        {!isEmpty(lenderResponsesWithLeasingBids) && (
          <Bids
            lenderResponses={lenderResponsesWithLeasingBids ?? []}
            title="Your leasing offers"
            lenderApplication={lenderApplication}
          />
        )}
        {!isEmpty(lenderResponsesWithRevolvingCreditBids) && (
          <Bids
            lenderResponses={lenderResponsesWithRevolvingCreditBids ?? []}
            title="Your revolving credit offers"
            lenderApplication={lenderApplication}
          />
        )}

        {!isEmpty(lenderResponsesWithPurchaseFinancingBids) && (
          <Bids
            lenderResponses={lenderResponsesWithPurchaseFinancingBids ?? []}
            title="Your purchase financing offers"
            lenderApplication={lenderApplication}
          />
        )}

        {!isEmpty(lenderResponsesWithMultipleBids) && (
          <Bids
            lenderResponses={lenderResponsesWithMultipleBids ?? []}
            title="Your combo offers"
            lenderApplication={lenderApplication}
          />
        )}

        {![
          LenderApplicationTypeEnum.REJECTED,
          LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_PAYOUT_RECEIVED_BY_CUSTOMER,
          LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_BID_SELECTED,
          LenderApplicationTypeEnum.OTHER_LENDER_APPLICATION_PAYOUT_RECEIVED_BY_CUSTOMER,
        ].includes(lenderApplication.type) ? (
          <>
            {(!isEmpty(lenderResponsesWithLoanBids) ||
              !isEmpty(lenderResponsesWithFactoringBids) ||
              !isEmpty(lenderResponsesWithRealEstateBids) ||
              !isEmpty(lenderResponsesWithLeasingBids) ||
              !isEmpty(lenderResponsesWithRevolvingCreditBids)) && (
              <div className="flex justify-center pb-5 xl:justify-start">
                <Button variant="default" onClick={placeAnotherBid}>
                  Place another bid
                </Button>
              </div>
            )}

            <FormSwitcher lenderApplication={lenderApplication} />
          </>
        ) : null}
      </div>
    </Form>
  );
};

export default LenderApplicationDetails;
